import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { FlightFareSelectionFunction } from "@b2bportal/core-types";
import {
  FlightRawEventName,
  useEmitCoreFlightRawEvent,
} from "../../../tracking";
import {
  setChosenOutgoingSlice,
  setChosenReturnSlice,
  setSelectedMulticityTrip,
} from "../store";
import { getFlightsData, getMulticityFlightsData } from "../store/selectors";

export const useSelectDepartureFlight = (): FlightFareSelectionFunction => {
  const dispatch = useDispatch();

  const flightsData = useSelector(getFlightsData);
  const multicityFlightsData = useSelector(getMulticityFlightsData);

  return useCallback(
    ({ sliceId, fareId, tripId }) => {
      if (flightsData != null) {
        const fareShelf =
          flightsData.fareSlices[flightsData.fares[fareId].outbound].fareShelf
            .value;
        dispatch(
          setChosenOutgoingSlice({
            outgoingSliceId: sliceId,
            outgoingFareId: fareId,
            tripId,
            outgoingFareRating: fareShelf,
          })
        );
      } else if (multicityFlightsData != null) {
        const fareShelf =
          multicityFlightsData.fareSlices[
            multicityFlightsData.fares[fareId].fareSlices[0]
          ].fareShelf.value;
        dispatch(
          setSelectedMulticityTrip({
            selectedTrip: {
              tripId,
              outgoingSliceId: sliceId,
              outgoingFareId: fareId,
              outgoingFareRating: fareShelf,
            },
            index: 0,
          })
        );
      }
      return;
    },
    [dispatch, flightsData, multicityFlightsData]
  );
};

export const useSelectReturnFlight = (
  multicityIndex: number = 1
): FlightFareSelectionFunction => {
  const dispatch = useDispatch();

  const flightsData = useSelector(getFlightsData);
  const multicityFlightsData = useSelector(getMulticityFlightsData);

  return useCallback(
    ({ sliceId, fareId, tripId }) => {
      if (flightsData != null) {
        const fareShelf =
          flightsData.fareSlices[flightsData.fares[fareId].outbound].fareShelf
            .value;
        dispatch(
          setChosenReturnSlice({
            returnSliceId: sliceId,
            returnFareId: fareId,
            returnFareRating: fareShelf,
            tripId,
          })
        );
      } else if (multicityFlightsData != null) {
        const fareShelf =
          multicityFlightsData.fareSlices[
            multicityFlightsData.fares[fareId].fareSlices[multicityIndex]
          ].fareShelf.value;

        dispatch(
          setSelectedMulticityTrip({
            selectedTrip: {
              tripId,
              outgoingSliceId: sliceId,
              outgoingFareId: fareId,
              outgoingFareRating: fareShelf,
            },
            index: multicityIndex,
          })
        );
      }
      return;
    },
    [dispatch, flightsData, multicityFlightsData, multicityIndex]
  );
};

export const useChooseFare = (goToNextStep: () => void) => {
  const emitSelectedFlightEvent = useEmitCoreFlightRawEvent(
    FlightRawEventName.SelectedFlight
  );

  return useCallback(() => {
    emitSelectedFlightEvent();

    goToNextStep();
  }, [goToNextStep, emitSelectedFlightEvent]);
};
